import { useEffect, useState } from 'react';
import { loadVersionPolicy, type VersionPolicy } from '@/api/appVersion';
import type { NativeVersionInfo } from '@/lib/nativeVersion';

/**
 * Busca a régua de versão uma vez por abertura do app.
 *
 * Promise singleton em escopo de módulo (mesmo idioma do `push.ts`): dois
 * consumidores — o gate duro no `App.tsx` e a faixa soft no `AppLayout` —
 * compartilham **uma** requisição.
 *
 * Deliberadamente NÃO bloqueia o boot: roda depois do mount. `null` enquanto
 * carrega e `null` pra sempre se não houver nada a mostrar.
 */

export type VersionPolicyState = {
  info: NativeVersionInfo;
  policy: VersionPolicy;
} | null;

let pending: Promise<VersionPolicyState> | null = null;

function load(): Promise<VersionPolicyState> {
  if (pending === null) {
    pending = loadVersionPolicy().catch(() => null);
  }
  return pending;
}

/** Devolve versão nativa + veredito, ou `null` (carregando ou nada a mostrar).
 *  O `info` vem junto porque o link padrão da loja depende da plataforma. */
export function useVersionPolicy(): VersionPolicyState {
  const [state, setState] = useState<VersionPolicyState>(null);

  useEffect(() => {
    let alive = true;
    void load().then((result) => {
      if (alive && result !== null) setState(result);
    });
    return () => {
      alive = false;
    };
  }, []);

  return state;
}

/** Só para testes. */
export function __resetVersionPolicyCache(): void {
  pending = null;
}
